import Moment from 'moment';
import { FUNDED_STATUSES } from '../constants/loanStatus';

export default {
    props: {
        loan: {
            type: Object,
            default: () => null
        },
        loans: {
            type: Array,
            default: () => null
        }
    },

    computed: {
        /**
         * @returns {string}
         */
        balanceEffective() {
            if (! this.pastPayments.length) {
                return this.$root.$options.filters.date(this.defaultLoan.started_at, 'MMM Do, YYYY');
            }

            return this.$root.$options.filters.date(this.pastPayments.slice(-1).pop().due_at, 'MMM Do, YYYY');
        },

        /**
         * @returns {Number}
         */
        originalBalance() {
            return Number(this.defaultLoan?.amount);
        },

        /**
         * @returns {Number}
         */
        remainingBalance() {
            if (this.loan && ['denied', 'denied_manual', 'closed', 'closed_remarketable'].includes(this.loan.status)) return null;
            else if (! this.loan && this.loans.length && ['denied', 'denied_manual', 'closed', 'closed_remarketable'].includes(this.loans[0].status)) return null;

            if (! this.isFunded) {
                if (this.loan) return Number(this.loan.amount);
                if (this.loans.length) return Number(this.loans[0].amount);
            }

            let fee = 0;

            if (this.loan) {
                fee = this.loan.fee;
            } else if (this.loans.length) {
                fee = this.loans[0].fee;
            }

            let originalBalance = this.originalBalance + fee;

            for (let i = 0; i < this.pastPostedPayments.length; i++) {
                originalBalance = originalBalance - Number(this.pastPostedPayments[i].principal);
            }

            return originalBalance;
        },

        /**
         * The amount of principal remaining as a percentage of the original principal.
         *
         * @returns {String}
         */
        percentOfLoanPaid() {
            let fee = 0;

            if (this.loan) {
                fee = this.loan.fee;
            } else if (this.loans.length) {
                fee = this.loans[0].fee;
            }

            let totalPaid = this.originalBalance + Number(fee) - this.remainingBalance,
                totalLeft = this.originalBalance - totalPaid,
                percentLeft = (totalLeft / this.originalBalance) * 100;

            return `${parseFloat(percentLeft).toFixed(2)}%`;
        },

        /**
         * @returns {Array}
         */
        pastPayments() {
            if (! this.defaultLoan || ! this.defaultLoan.payments || ! this.defaultLoan.payments.length) {
                return [];
            }

            return this.defaultLoan.payments.filter(payment => {
                if (payment.computed_amount < 0) {
                    return false;
                }

                return Moment(payment.due_at).isBefore(Moment());
            });
        },

        /**
         * @returns {Array}
         */
        postedPayments() {
            if (! this.defaultLoan || ! this.defaultLoan.payments || ! this.defaultLoan.payments.length) {
                return [];
            }

            return this.defaultLoan.payments.filter(payment => payment.status === 'posted');
        },

        /**
         * @returns {Array}
         */
        pastPostedPayments() {
            return this.pastPayments.filter(payment => payment.status === 'posted');
        },

        /**
         * @returns {Array}
         */
        pastPastDuePayments() {
            return this.pastPayments.filter(payment => payment.status === 'past_due');
        },

        /**
         * @returns {Array}
         */
        futurePayments() {
            if (! this.defaultLoan || ! this.defaultLoan.payments || ! this.defaultLoan.payments.length) {
                return [];
            }

            return this.defaultLoan.payments.filter(payment => {
                if (payment.computed_amount < 0) {
                    return false;
                }

                return Moment(payment.due_at).isAfter(Moment());
            });
        },

        futurePendingPayments() {
            return _.sortBy(this.futurePayments.filter(payment => ['pending', 'processed'].includes(payment.status)), 'due_at');
        },

        isLoanActive() {
            return !['paused', 'closed', 'closed_remarketable', 'expired', 'denied', 'denied_manual'].includes(this.loan.status);
        },

        isFunded() {
            const status = this.loan?.status ?? this.loans?.[0]?.status;
            return FUNDED_STATUSES.includes(status);
        },

        isLoanPaidInFull() {
            return ['closed', 'closed_remarketable'].includes(this.loan.status) && this.loan.pif_date;
        }
    },

    data() {
        return {
            defaultLoan: this.loan,
            pastDue: false
        }
    },

    methods: {
        /**
         * @param {String} subType
         * @returns {String}
         */
        formatSubType(subType) {
            switch (subType) {
                case 'cd':
                    return 'CD';
                default:
                    return this.$options.filters.ucfirst(subType);
            }
        },
        
        formatDate(date, format = 'MMM Do, YYYY') {
            return this.$options.filters.date(date, format);
        }
    }
}